import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Search, MapPin, Phone, Star, Tag, ChevronRight, Building2, Filter, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { AdCarousel, SidebarAd, FeaturedAd } from '../components/AdComponents';
import { FAVICON } from '../assets';
import axios from 'axios';
import './landing-vip.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DirectoryPage = () => {
  const { i18n } = useTranslation();
  const isSpanish = i18n.language?.startsWith('es');
  
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [businessTypes, setBusinessTypes] = useState([]);
  const [selectedType, setSelectedType] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Content translations
  const content = {
    es: {
      heroEyebrow: "Directorio Local",
      heroTitle: <>Descubre los comercios<br/>que valoran tu lealtad</>,
      heroAccent: "Tu barrio está lleno de oportunidades.",
      heroSub: <>Explora negocios locales verificados que ofrecen <strong>experiencias auténticas</strong> y recompensan tu fidelidad con cada visita.</>,
      searchPlaceholder: "Buscar por nombre, tipo o servicio...",
      searchBtn: "Buscar",
      filterLabel: "Filtrar:",
      filterAll: "Todos",
      noResults: "No se encontraron negocios",
      noResultsSub: "Prueba con otros términos de búsqueda",
      kupons: "kupones activos",
      kupon: "kupon activo",
      viewProfile: "Ver perfil",
      reviews: "reseñas",
      prevPage: "Anterior",
      nextPage: "Siguiente",
      ctaTitle: <>¿Tienes un negocio local?</>,
      ctaSub: "Únete al directorio y conecta con clientes que valoran el comercio de proximidad.",
      ctaBtn: "Registrar mi Negocio"
    },
    en: {
      heroEyebrow: "Local Directory",
      heroTitle: <>Discover businesses<br/>that value your loyalty</>,
      heroAccent: "Your neighbourhood is full of opportunities.",
      heroSub: <>Explore verified local businesses offering <strong>authentic experiences</strong> that reward your loyalty with every visit.</>,
      searchPlaceholder: "Search by name, type or service...",
      searchBtn: "Search",
      filterLabel: "Filter:",
      filterAll: "All",
      noResults: "No businesses found",
      noResultsSub: "Try different search terms",
      kupons: "active coupons",
      kupon: "active coupon",
      viewProfile: "View profile",
      reviews: "reviews",
      prevPage: "Previous",
      nextPage: "Next",
      ctaTitle: <>Own a local business?</>,
      ctaSub: "Join our directory and connect with customers who value local commerce.",
      ctaBtn: "Register my Business"
    }
  };

  const c = isSpanish ? content.es : content.en;

  useEffect(() => {
    fetchBusinessTypes();
  }, []);

  useEffect(() => {
    fetchBusinesses();
  }, [page, selectedType]);

  const fetchBusinessTypes = async () => {
    try {
      const { data } = await axios.get(`${API}/directory/business-types`);
      setBusinessTypes(data.types || []);
    } catch (err) {
      console.error('Error fetching types:', err);
    }
  };

  const fetchBusinesses = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (selectedType) params.append('business_type', selectedType);
      params.append('page', page);
      params.append('limit', 12);

      const { data } = await axios.get(`${API}/directory/businesses?${params}`);
      setBusinesses(data.businesses || []);
      setTotalPages(data.pages || 1);
    } catch (err) {
      console.error('Error fetching businesses:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchBusinesses();
  };

  return (
    <div className="vip-landing">
      {/* Hero Section */}
      <section className="vip-hero" style={{ minHeight: 'auto', padding: '120px 24px 80px' }}>
        <div className="vip-hero-bg">
          <div className="vip-hero-gradient" />
          <div className="vip-hero-pattern" />
        </div>
        
        <div className="vip-hero-content" style={{ maxWidth: '800px' }}>
          <motion.div 
            className="vip-eyebrow"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Sparkles className="vip-eyebrow-icon" />
            {c.heroEyebrow}
          </motion.div>
          
          <motion.h1 
            className="vip-hero-title"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            style={{ fontSize: 'clamp(2rem, 5vw, 3.5rem)' }}
          >
            {c.heroTitle}
          </motion.h1>
          
          <motion.p 
            className="vip-hero-accent"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            style={{ fontSize: 'clamp(1.2rem, 2.5vw, 1.5rem)', marginBottom: '32px' }}
          >
            {c.heroAccent}
          </motion.p>
          
          <motion.p 
            className="vip-hero-sub"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            style={{ marginBottom: '40px' }}
          >
            {c.heroSub}
          </motion.p>

          {/* Search Bar */}
          <motion.form 
            onSubmit={handleSearch}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            style={{ maxWidth: '600px', margin: '0 auto' }}
          >
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <div style={{ position: 'relative', flex: 1, minWidth: '200px' }}>
                <Search style={{ 
                  position: 'absolute', 
                  left: '16px', 
                  top: '50%', 
                  transform: 'translateY(-50%)',
                  width: '20px',
                  height: '20px',
                  color: 'rgba(255,255,255,0.5)'
                }} />
                <input
                  type="text"
                  placeholder={c.searchPlaceholder}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  data-testid="directory-search"
                  style={{
                    width: '100%',
                    padding: '16px 16px 16px 48px',
                    fontSize: '16px',
                    background: 'rgba(255,255,255,0.1)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: '8px',
                    color: '#fff',
                    outline: 'none',
                    transition: 'all 0.3s ease'
                  }}
                />
              </div>
              <button 
                type="submit"
                data-testid="directory-search-btn"
                style={{
                  padding: '16px 32px',
                  background: 'linear-gradient(135deg, #c9a962 0%, #a88b3d 100%)',
                  color: '#0a1628',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
              >
                {c.searchBtn}
              </button>
            </div>
          </motion.form>
        </div>
      </section>

      {/* Filters */}
      <div style={{ 
        background: '#fff', 
        borderBottom: '1px solid #e8e4db',
        position: 'sticky',
        top: '64px',
        zIndex: 30
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '16px 24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', overflowX: 'auto', paddingBottom: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#6b7280', fontSize: '14px', flexShrink: 0 }}>
              <Filter style={{ width: '16px', height: '16px' }} />
              <span>{c.filterLabel}</span>
            </div>
            <button
              onClick={() => { setSelectedType(''); setPage(1); }}
              style={{
                padding: '8px 16px',
                background: selectedType === '' ? '#0a1628' : 'transparent',
                color: selectedType === '' ? '#fff' : '#0a1628',
                border: '1px solid',
                borderColor: selectedType === '' ? '#0a1628' : '#e8e4db',
                borderRadius: '20px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                transition: 'all 0.2s ease'
              }}
            >
              {c.filterAll}
            </button>
            {businessTypes.slice(0, 8).map((type) => (
              <button
                key={type}
                onClick={() => { setSelectedType(type); setPage(1); }}
                style={{
                  padding: '8px 16px',
                  background: selectedType === type ? '#0a1628' : 'transparent',
                  color: selectedType === type ? '#fff' : '#0a1628',
                  border: '1px solid',
                  borderColor: selectedType === type ? '#0a1628' : '#e8e4db',
                  borderRadius: '20px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.2s ease'
                }}
              >
                {type}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Business Grid */}
      <section style={{ background: '#f8f6f1', padding: '60px 24px', minHeight: '60vh' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          
          {/* Ad Carousel - Top of listings */}
          <AdCarousel className="mb-8" variant="compact" />
          
          {loading ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '24px' }}>
              {[...Array(8)].map((_, i) => (
                <div key={i} style={{ 
                  background: '#fff', 
                  borderRadius: '16px', 
                  height: '380px',
                  animation: 'pulse 2s infinite'
                }} />
              ))}
            </div>
          ) : businesses.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '80px 20px' }}>
              <Building2 style={{ width: '64px', height: '64px', margin: '0 auto 16px', color: '#e8e4db' }} />
              <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: '24px', color: '#0a1628', marginBottom: '8px' }}>
                {c.noResults}
              </h3>
              <p style={{ color: '#6b7280' }}>{c.noResultsSub}</p>
            </div>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '24px' }}>
                {businesses.map((business, index) => (
                  <motion.div
                    key={business.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Link to={`/negocio/${business.id}`} data-testid={`business-card-${business.id}`} style={{ textDecoration: 'none' }}>
                      <div style={{
                        background: '#fff',
                        borderRadius: '16px',
                        overflow: 'hidden',
                        border: '1px solid #e8e4db',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer'
                      }}
                      className="directory-card"
                      >
                        {/* Cover Image */}
                        <div style={{ position: 'relative', height: '140px', background: 'linear-gradient(135deg, #0a1628 0%, #1a2d4a 100%)' }}>
                          {business.coverImage ? (
                            <img 
                              src={business.coverImage} 
                              alt={business.businessName}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          ) : (
                            <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <Building2 style={{ width: '40px', height: '40px', color: 'rgba(201, 169, 98, 0.3)' }} />
                            </div>
                          )}
                          
                          {/* Logo */}
                          <div style={{
                            position: 'absolute',
                            bottom: '-28px',
                            left: '20px',
                            width: '56px',
                            height: '56px',
                            borderRadius: '12px',
                            background: '#fff',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                            border: '3px solid #fff'
                          }}>
                            {business.logo ? (
                              <img src={business.logo} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                              <span style={{ fontSize: '24px', fontWeight: '700', color: '#c9a962', fontFamily: "'Playfair Display', serif" }}>
                                {business.businessName?.charAt(0)}
                              </span>
                            )}
                          </div>

                          {/* Active Kupons Badge */}
                          {business.activeKuponsCount > 0 && (
                            <div style={{
                              position: 'absolute',
                              top: '12px',
                              right: '12px',
                              background: '#c9a962',
                              color: '#0a1628',
                              fontSize: '11px',
                              fontWeight: '600',
                              padding: '6px 12px',
                              borderRadius: '20px'
                            }}>
                              {business.activeKuponsCount} {business.activeKuponsCount !== 1 ? c.kupons : c.kupon}
                            </div>
                          )}
                        </div>

                        {/* Content */}
                        <div style={{ padding: '36px 20px 20px' }}>
                          <h3 style={{ 
                            fontFamily: "'Playfair Display', serif",
                            fontSize: '18px', 
                            color: '#0a1628', 
                            marginBottom: '4px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {business.businessName}
                          </h3>
                          
                          {business.businessType && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#a88b3d', marginBottom: '12px' }}>
                              <Tag style={{ width: '12px', height: '12px' }} />
                              <span>{business.businessType}</span>
                            </div>
                          )}

                          {business.description && (
                            <p style={{ 
                              fontSize: '14px', 
                              color: '#6b7280', 
                              marginBottom: '16px',
                              lineHeight: '1.5',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden'
                            }}>
                              {business.description}
                            </p>
                          )}

                          {/* NAP Info */}
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '13px', color: '#6b7280' }}>
                            {(business.address || business.city) && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <MapPin style={{ width: '14px', height: '14px', flexShrink: 0, color: '#9ca3af' }} />
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {business.address || business.city}
                                </span>
                              </div>
                            )}
                            {business.phone && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Phone style={{ width: '14px', height: '14px', flexShrink: 0, color: '#9ca3af' }} />
                                <span>{business.phone}</span>
                              </div>
                            )}
                          </div>

                          {/* Rating */}
                          {business.averageRating > 0 && (
                            <div style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '8px', 
                              marginTop: '16px', 
                              paddingTop: '16px', 
                              borderTop: '1px solid #e8e4db' 
                            }}>
                              <Star style={{ width: '16px', height: '16px', fill: '#c9a962', color: '#c9a962' }} />
                              <span style={{ fontWeight: '600', color: '#0a1628' }}>{business.averageRating.toFixed(1)}</span>
                              <span style={{ fontSize: '13px', color: '#6b7280' }}>
                                ({business.totalReviews} {c.reviews})
                              </span>
                            </div>
                          )}

                          {/* CTA */}
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'flex-end', 
                            marginTop: '16px',
                            color: '#c9a962',
                            fontWeight: '500',
                            fontSize: '14px'
                          }}>
                            {c.viewProfile}
                            <ChevronRight style={{ width: '16px', height: '16px', marginLeft: '4px' }} />
                          </div>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>

              {/* Featured Ads - After listings */}
              <FeaturedAd className="mt-8" limit={2} />

              {/* Pagination */}
              {totalPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginTop: '48px' }}>
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    style={{
                      padding: '12px 24px',
                      background: 'transparent',
                      color: page === 1 ? '#9ca3af' : '#0a1628',
                      border: '1px solid',
                      borderColor: page === 1 ? '#e8e4db' : '#0a1628',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: page === 1 ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {c.prevPage}
                  </button>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {[...Array(Math.min(5, totalPages))].map((_, i) => {
                      const pageNum = i + 1;
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setPage(pageNum)}
                          style={{
                            width: '40px',
                            height: '40px',
                            background: page === pageNum ? '#0a1628' : 'transparent',
                            color: page === pageNum ? '#fff' : '#0a1628',
                            border: '1px solid',
                            borderColor: page === pageNum ? '#0a1628' : '#e8e4db',
                            borderRadius: '8px',
                            fontSize: '14px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    style={{
                      padding: '12px 24px',
                      background: 'transparent',
                      color: page === totalPages ? '#9ca3af' : '#0a1628',
                      border: '1px solid',
                      borderColor: page === totalPages ? '#e8e4db' : '#0a1628',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: page === totalPages ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {c.nextPage}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* CTA for Business Owners */}
      <section className="vip-final" style={{ padding: '100px 24px' }}>
        <div className="vip-final-bg" />
        <div className="vip-final-content">
          <span className="vip-eyebrow-small vip-gold">{isSpanish ? 'Para Negocios' : 'For Businesses'}</span>
          <h2 className="vip-final-title">{c.ctaTitle}</h2>
          <p className="vip-final-sub">{c.ctaSub}</p>
          <Link to="/para-negocios" className="vip-btn-final" data-testid="directory-business-cta">
            {c.ctaBtn}
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ background: '#0a1628', borderTop: '1px solid rgba(255,255,255,0.1)', padding: '32px 24px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <img src={FAVICON} alt="LokAlGrow" style={{ width: '32px', height: '32px', borderRadius: '8px' }} />
            <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px' }}>© {new Date().getFullYear()} LokAlGrow</span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px', fontSize: '14px' }}>
            <Link to="/privacy" style={{ color: 'rgba(255,255,255,0.5)', textDecoration: 'none' }}>
              {isSpanish ? 'Privacidad' : 'Privacy'}
            </Link>
            <Link to="/terms" style={{ color: 'rgba(255,255,255,0.5)', textDecoration: 'none' }}>
              {isSpanish ? 'Términos' : 'Terms'}
            </Link>
            <Link to="/cookies" style={{ color: 'rgba(255,255,255,0.5)', textDecoration: 'none' }}>
              Cookies
            </Link>
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        .directory-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 40px rgba(0,0,0,0.1);
          border-color: #c9a962 !important;
        }
        input::placeholder {
          color: rgba(255,255,255,0.4);
        }
        input:focus {
          border-color: rgba(201, 169, 98, 0.5) !important;
          background: rgba(255,255,255,0.15) !important;
        }
      `}</style>
    </div>
  );
};

export default DirectoryPage;
